import { createContext, isValidElement, useCallback, useContext, useId, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from './cn'
import { IconAlert, IconCheck, IconClose, IconInfo } from './Icons'

type ToastVariant = 'success' | 'error' | 'info' | 'warning'

/** True for a React element (so it is treated as a message, not a ToastInput object). */
function isReactElement(value: unknown): boolean {
  return isValidElement(value)
}

export interface ToastItem {
  id: string
  title?: ReactNode
  description?: ReactNode
  variant: ToastVariant
  duration: number
}

export interface ToastInput {
  title?: ReactNode
  description?: ReactNode
  variant?: ToastVariant
  /** ms before auto-dismiss. Default 5000. Set 0 to disable. */
  duration?: number
}

/**
 * Options accepted alongside a plain message in the 2-argument helper form
 * `success(message, { title, duration })`. This is the fork (coaching /
 * consulting) Toast contract; it is a strict subset of {@link ToastInput} minus
 * the message field, so the two helper overloads share one implementation.
 */
export interface ToastOptions {
  /** Optional bold heading above the message. */
  title?: ReactNode
  /** ms before auto-dismiss. Default 5000. Set 0 to disable. */
  duration?: number
}

/**
 * Helper signature — backward-compatible SUPERSET of two production contracts:
 *  - canonical 1-arg: `success({ title, description, duration })` or `success('msg')`
 *  - fork 2-arg:      `success('msg', { title, duration })`
 * The message (ReactNode) maps to the toast `description`.
 */
export type ToastHelper = (input: ToastInput | ReactNode, options?: ToastOptions) => string

export interface ToastContextValue {
  success: ToastHelper
  error: ToastHelper
  info: ToastHelper
  warning: ToastHelper
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const variantStyles: Record<ToastVariant, { bar: string; icon: ReactNode; label: string }> = {
  success: {
    bar: 'bg-ok',
    icon: <IconCheck size={14} />,
    label: 'Success',
  },
  error: {
    bar: 'bg-err',
    icon: <IconClose size={14} />,
    label: 'Error',
  },
  warning: {
    bar: 'bg-mustard',
    icon: <IconAlert size={14} />,
    label: 'Warning',
  },
  info: {
    bar: 'bg-info',
    icon: <IconInfo size={14} />,
    label: 'Information',
  },
}

export interface ToastProviderProps {
  children: ReactNode
  /** container position — defaults to bottom-right. */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
}

export function ToastProvider({ children, position = 'bottom-right' }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const counter = useRef(0)

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const push = useCallback(
    (variant: ToastVariant, input: ToastInput | ReactNode, options?: ToastOptions) => {
      const id = `toast-${++counter.current}`
      // Distinguish the canonical 1-arg ToastInput object from a bare ReactNode
      // message. The 2-arg form (options present) is always message+options. A
      // plain object with no React-element marker is treated as a ToastInput.
      const isToastInput =
        options === undefined &&
        typeof input === 'object' &&
        input !== null &&
        !isReactElement(input) &&
        !Array.isArray(input)
      const norm: ToastInput = isToastInput
        ? (input as ToastInput)
        : { description: input as ReactNode, ...options }
      const item: ToastItem = {
        id,
        title: norm.title,
        description: norm.description,
        variant,
        duration: norm.duration ?? 5000,
      }
      setToasts((prev) => [...prev, item])
      if (item.duration > 0) {
        const t = setTimeout(() => dismiss(id), item.duration)
        timers.current.set(id, t)
      }
      return id
    },
    [dismiss]
  )

  const ctx = useMemo<ToastContextValue>(
    () => ({
      success: (input, options) => push('success', input, options),
      error: (input, options) => push('error', input, options),
      info: (input, options) => push('info', input, options),
      warning: (input, options) => push('warning', input, options),
      dismiss,
    }),
    [push, dismiss]
  )

  const positionClass = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  }[position]

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className={cn('fixed z-toast flex flex-col gap-2 max-w-sm', positionClass)}
      >
        {toasts.map((t) => {
          const v = variantStyles[t.variant]
          return (
            <ToastCard key={t.id} toast={t} variantIcon={v.icon} variantBar={v.bar} variantLabel={v.label} onDismiss={() => dismiss(t.id)} />
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

interface ToastCardProps {
  toast: ToastItem
  variantIcon: ReactNode
  variantBar: string
  variantLabel: string
  onDismiss: () => void
}

function ToastCard({ toast, variantIcon, variantBar, variantLabel, onDismiss }: ToastCardProps) {
  const titleId = useId()
  const descId = useId()
  return (
    <div
      role="status"
      aria-labelledby={toast.title ? titleId : undefined}
      aria-describedby={toast.description ? descId : undefined}
      className={cn(
        'relative flex w-full overflow-hidden rounded-control border border-line bg-white shadow-2',
        'animate-fade-in motion-reduce:animate-none'
      )}
    >
      <div className={cn('w-1 shrink-0', variantBar)} aria-hidden="true" />
      <div className="flex items-start gap-3 p-3 flex-1">
        <span className={cn('mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white', variantBar)} aria-hidden="true">
          {variantIcon}
        </span>
        <div className="flex-1 min-w-0">
          <span className="sr-only">{variantLabel}:</span>
          {toast.title && (
            <div id={titleId} className="text-sm font-medium text-strong">
              {toast.title}
            </div>
          )}
          {toast.description && (
            <div id={descId} className={cn('text-sm text-ink-body', toast.title ? 'mt-0.5' : '')}>
              {toast.description}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="shrink-0 inline-flex size-8 -m-1.5 items-center justify-center rounded-control text-ink-muted hover:text-strong hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus coarse:min-h-tap coarse:min-w-tap"
        >
          <IconClose size={16} />
        </button>
      </div>
    </div>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>. Wrap your app root: <ToastProvider><App /></ToastProvider>')
  }
  return ctx
}
